import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Book, Keyboard, Type, Volume2, HeadphonesIcon, Smartphone, Monitor } from 'lucide-react';

export default function TamilHelp() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('typing');

  return (
    <div className="container mx-auto py-8 max-w-4xl">
      <div className="mb-8 text-center">
        <h1 className={`text-4xl font-bold text-gray-900 dark:text-white mb-4 ${t('language') === 'ta' ? 'tamil-heading' : ''}`}>
          {t('help.tamilLanguageHelp')}
        </h1>
        <p className={`text-lg text-gray-600 dark:text-gray-400 ${t('language') === 'ta' ? 'tamil-text' : ''}`}>
          வோட்டர்வெரிஃபை பயன்பாட்டில் தமிழில் எப்படி பயன்படுத்துவது என்பதற்கான வழிகாட்டி
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="typing" className={t('language') === 'ta' ? 'tamil-ui' : ''}>
            <Type className="h-4 w-4 mr-2" />
            {t('help.tamilInputInstructions')}
          </TabsTrigger>
          <TabsTrigger value="keyboard" className={t('language') === 'ta' ? 'tamil-ui' : ''}>
            <Keyboard className="h-4 w-4 mr-2" />
            {t('help.tamilKeyboardGuide')}
          </TabsTrigger>
          <TabsTrigger value="fonts" className={t('language') === 'ta' ? 'tamil-ui' : ''}>
            <Book className="h-4 w-4 mr-2" />
            {t('help.tamilFonts')}
          </TabsTrigger>
          <TabsTrigger value="mobile" className={t('language') === 'ta' ? 'tamil-ui' : ''}>
            <Smartphone className="h-4 w-4 mr-2" />
            {t('help.fontOptimization')}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="typing" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className={`flex items-center gap-2 ${t('language') === 'ta' ? 'tamil-heading' : ''}`}>
                <Type className="h-5 w-5" />
                {t('help.howToTypeInTamil')}
              </CardTitle>
              <CardDescription className={t('language') === 'ta' ? 'tamil-text' : ''}>
                {t('help.phoneticTyping')}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-3">
                  <h3 className={`font-semibold text-lg ${t('language') === 'ta' ? 'tamil-heading' : ''}`}>
                    எளிய படிகள்:
                  </h3>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline">1</Badge>
                      <span className={t('language') === 'ta' ? 'tamil-text' : ''}>
                        {t('help.step1')}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline">2</Badge>
                      <span className={t('language') === 'ta' ? 'tamil-text' : ''}>
                        {t('help.step2')}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline">3</Badge>
                      <span className={t('language') === 'ta' ? 'tamil-text' : ''}>
                        {t('help.step3')}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline">4</Badge>
                      <span className={t('language') === 'ta' ? 'tamil-text' : ''}>
                        {t('help.step4')}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  <h3 className={`font-semibold text-lg ${t('language') === 'ta' ? 'tamil-heading' : ''}`}>
                    எடுத்துக்காட்டுகள்:
                  </h3>
                  <div className="space-y-1 font-mono text-sm">
                    <div className="flex justify-between">
                      <span>vanakkam</span>
                      <span className="tamil-text">→ வணக்கம்</span>
                    </div>
                    <div className="flex justify-between">
                      <span>nantri</span>
                      <span className="tamil-text">→ நன்றி</span>
                    </div>
                    <div className="flex justify-between">
                      <span>tamil</span>
                      <span className="tamil-text">→ தமிழ்</span>
                    </div>
                    <div className="flex justify-between">
                      <span>ennai</span>
                      <span className="tamil-text">→ என்னை</span>
                    </div>
                    <div className="flex justify-between">
                      <span>kaapi</span>
                      <span className="tamil-text">→ காபி</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                <h4 className="font-semibold text-blue-900 dark:text-blue-100 mb-2">
                  💡 உதவிக்குறிப்பு:
                </h4>
                <p className="text-blue-800 dark:text-blue-200 text-sm">
                  உங்களால் தமிழில் தட்டச்சு செய்யத் தொடங்க, மேலே உள்ள மொழி தேர்வியில் "தமிழ்" என்பதைத் தேர்ந்தெடுக்கவும்.
                  பிறகு எந்தவொரு உரைப் புலத்திலும் ஆங்கில எழுத்துகளில் தட்டச்சு செய்தால், அது தானாகவே தமிழில் மாற்றப்படும்.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="keyboard" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className={`flex items-center gap-2 ${t('language') === 'ta' ? 'tamil-heading' : ''}`}>
                <Keyboard className="h-5 w-5" />
                தமிழ் ஒலிப்பியல் விசைப்பலகை வரைபடம்
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Accordion type="single" collapsible className="w-full">
                <AccordionItem value="vowels">
                  <AccordionTrigger className={t('language') === 'ta' ? 'tamil-text' : ''}>
                    உயிரெழுத்துகள் (Vowels)
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                      <div className="flex justify-between p-2 bg-gray-50 rounded">
                        <span>a</span>
                        <span className="tamil-text">அ</span>
                      </div>
                      <div className="flex justify-between p-2 bg-gray-50 rounded">
                        <span>aa</span>
                        <span className="tamil-text">ஆ</span>
                      </div>
                      <div className="flex justify-between p-2 bg-gray-50 rounded">
                        <span>i</span>
                        <span className="tamil-text">இ</span>
                      </div>
                      <div className="flex justify-between p-2 bg-gray-50 rounded">
                        <span>ii</span>
                        <span className="tamil-text">ஈ</span>
                      </div>
                      <div className="flex justify-between p-2 bg-gray-50 rounded">
                        <span>u</span>
                        <span className="tamil-text">உ</span>
                      </div>
                      <div className="flex justify-between p-2 bg-gray-50 rounded">
                        <span>uu</span>
                        <span className="tamil-text">ஊ</span>
                      </div>
                      <div className="flex justify-between p-2 bg-gray-50 rounded">
                        <span>e</span>
                        <span className="tamil-text">எ</span>
                      </div>
                      <div className="flex justify-between p-2 bg-gray-50 rounded">
                        <span>ee</span>
                        <span className="tamil-text">ஏ</span>
                      </div>
                      <div className="flex justify-between p-2 bg-gray-50 rounded">
                        <span>ai</span>
                        <span className="tamil-text">ஐ</span>
                      </div>
                      <div className="flex justify-between p-2 bg-gray-50 rounded">
                        <span>o</span>
                        <span className="tamil-text">ஒ</span>
                      </div>
                      <div className="flex justify-between p-2 bg-gray-50 rounded">
                        <span>oo</span>
                        <span className="tamil-text">ஓ</span>
                      </div>
                      <div className="flex justify-between p-2 bg-gray-50 rounded">
                        <span>au</span>
                        <span className="tamil-text">ஔ</span>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="consonants">
                  <AccordionTrigger className={t('language') === 'ta' ? 'tamil-text' : ''}>
                    மெய்யெழுத்துகள் (Consonants)
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                      <div className="flex justify-between p-2 bg-gray-50 rounded">
                        <span>ka</span>
                        <span className="tamil-text">க</span>
                      </div>
                      <div className="flex justify-between p-2 bg-gray-50 rounded">
                        <span>ga</span>
                        <span className="tamil-text">க</span>
                      </div>
                      <div className="flex justify-between p-2 bg-gray-50 rounded">
                        <span>cha</span>
                        <span className="tamil-text">ச</span>
                      </div>
                      <div className="flex justify-between p-2 bg-gray-50 rounded">
                        <span>ja</span>
                        <span className="tamil-text">ஜ</span>
                      </div>
                      <div className="flex justify-between p-2 bg-gray-50 rounded">
                        <span>ta</span>
                        <span className="tamil-text">த</span>
                      </div>
                      <div className="flex justify-between p-2 bg-gray-50 rounded">
                        <span>da</span>
                        <span className="tamil-text">த</span>
                      </div>
                      <div className="flex justify-between p-2 bg-gray-50 rounded">
                        <span>tha</span>
                        <span className="tamil-text">த</span>
                      </div>
                      <div className="flex justify-between p-2 bg-gray-50 rounded">
                        <span>na</span>
                        <span className="tamil-text">ன</span>
                      </div>
                      <div className="flex justify-between p-2 bg-gray-50 rounded">
                        <span>pa</span>
                        <span className="tamil-text">ப</span>
                      </div>
                      <div className="flex justify-between p-2 bg-gray-50 rounded">
                        <span>ba</span>
                        <span className="tamil-text">ப</span>
                      </div>
                      <div className="flex justify-between p-2 bg-gray-50 rounded">
                        <span>ma</span>
                        <span className="tamil-text">ம</span>
                      </div>
                      <div className="flex justify-between p-2 bg-gray-50 rounded">
                        <span>ya</span>
                        <span className="tamil-text">ய</span>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="common">
                  <AccordionTrigger className={t('language') === 'ta' ? 'tamil-text' : ''}>
                    பொதுவான வார்த்தைகள் (Common Words)
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      <div className="flex justify-between p-2 bg-gray-50 rounded">
                        <span>vanakkam</span>
                        <span className="tamil-text">வணக்கம்</span>
                      </div>
                      <div className="flex justify-between p-2 bg-gray-50 rounded">
                        <span>nantri</span>
                        <span className="tamil-text">நன்றி</span>
                      </div>
                      <div className="flex justify-between p-2 bg-gray-50 rounded">
                        <span>saappadu</span>
                        <span className="tamil-text">சாப்பாடு</span>
                      </div>
                      <div className="flex justify-between p-2 bg-gray-50 rounded">
                        <span>paadam</span>
                        <span className="tamil-text">பாடம்</span>
                      </div>
                      <div className="flex justify-between p-2 bg-gray-50 rounded">
                        <span>pustagam</span>
                        <span className="tamil-text">புஸ்தகம்</span>
                      </div>
                      <div className="flex justify-between p-2 bg-gray-50 rounded">
                        <span>thelivu</span>
                        <span className="tamil-text">தெளிவு</span>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="fonts" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className={`flex items-center gap-2 ${t('language') === 'ta' ? 'tamil-heading' : ''}`}>
                <Book className="h-5 w-5" />
                தமிழ் எழுத்துருக்கள் மற்றும் காட்சி தேர்வுமுறை
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h3 className={`font-semibold text-lg mb-3 ${t('language') === 'ta' ? 'tamil-heading' : ''}`}>
                  பயன்படுத்தப்படும் எழுத்துருக்கள்:
                </h3>
                <div className="space-y-3">
                  <div className="p-4 border rounded-lg">
                    <h4 className="font-medium mb-2">Noto Sans Tamil</h4>
                    <p className="text-sm text-gray-600 mb-2">
                      Google ஆதரவுடன் வடிவமைக்கப்பட்ட நவீன தமிழ் எழுத்துரு
                    </p>
                    <div className="tamil-text text-lg">
                      வணக்கம்! இது நோடோ சான்ஸ் தமிழ் எழுத்துருவில் எழுதப்பட்டது.
                    </div>
                  </div>

                  <div className="p-4 border rounded-lg">
                    <h4 className="font-medium mb-2">எழுத்துரு அம்சங்கள்:</h4>
                    <ul className="text-sm space-y-1">
                      <li>✓ அனைத்து சாதனங்களிலும் சிறந்த வாசிப்புத்திறன்</li>
                      <li>✓ மொபைல் காட்சிகளுக்கு மேம்படுத்தப்பட்டது</li>
                      <li>✓ தெளிவான எழுத்துகள் மற்றும் சரியான விகிதாசாரம்</li>
                      <li>✓ ஆங்கிலம் மற்றும் தமிழ் கலந்த உரைக்கு சிறந்த ஆதரவு</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
                <h4 className="font-semibold text-green-900 dark:text-green-100 mb-2">
                  💻 எழுத்துரு மேம்படுத்தல்:
                </h4>
                <p className="text-green-800 dark:text-green-200 text-sm">
                  இந்தப் பயன்பாடு மொபைல் சாதனங்களில் சிறந்த வாசிப்புத்திறனுக்காக மேம்படுத்தப்பட்ட தமிழ் எழுத்துருக்களைப் பயன்படுத்துகிறது.
                  சிறிய திரைகளிலும் தெளிவாகப் படிக்க முடியும்.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="mobile" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className={`flex items-center gap-2 ${t('language') === 'ta' ? 'tamil-heading' : ''}`}>
                <Smartphone className="h-5 w-5" />
                மொபைல் சாதனங்களுக்கான தமிழ் ஆதரவு
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-3">
                  <h3 className={`font-semibold ${t('language') === 'ta' ? 'tamil-heading' : ''}`}>
                    📱 மொபைல் அம்சங்கள்:
                  </h3>
                  <ul className="text-sm space-y-2">
                    <li className="flex items-start gap-2">
                      <span className="text-green-500">✓</span>
                      <span className={t('language') === 'ta' ? 'tamil-text' : ''}>
                        தொடுதிரை சாதனங்களுக்கு மேம்படுத்தப்பட்ட இடைமுகம்
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-500">✓</span>
                      <span className={t('language') === 'ta' ? 'tamil-text' : ''}>
                        பெரிய, தெளிவான தமிழ் எழுத்துருக்கள்
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-500">✓</span>
                      <span className={t('language') === 'ta' ? 'tamil-text' : ''}>
                        விரைவான மொழி மாற்றம்
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-500">✓</span>
                      <span className={t('language') === 'ta' ? 'tamil-text' : ''}>
                        திரை சுழற்சிக்கு ஆதரவு (ஆங்கிலம்/தமிழ்)
                      </span>
                    </li>
                  </ul>
                </div>

                <div className="space-y-3">
                  <h3 className={`font-semibold ${t('language') === 'ta' ? 'tamil-heading' : ''}`}>
                    🎯 பயன்பாட்டு குறிப்புகள்:
                  </h3>
                  <div className="space-y-2 text-sm">
                    <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded">
                      <strong>தமிழ் தட்டச்சு:</strong> தமிழ் முறையில் தட்டச்சு செய்ய மேலே உள்ள மொழி பொத்தானை அழுத்தவும்
                    </div>
                    <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded">
                      <strong>ஸ்கிரீன் ரீடிங்:</strong> திரையை ஸ்கிரோல் செய்யும்போது தமிழ் உரை தெளிவாகத் தெரியும்
                    </div>
                    <div className="p-3 bg-purple-50 dark:bg-purple-900/20 rounded">
                      <strong>விசைப்பலகை:</strong> தமிழ் ஒன்ஸ்கிரீன் விசைப்பலகை கிடைக்கிறது
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-orange-50 dark:bg-orange-900/20 p-4 rounded-lg">
                <h4 className="font-semibold text-orange-900 dark:text-orange-100 mb-2">
                  📞 உதவி தேவையா?
                </h4>
                <p className="text-orange-800 dark:text-orange-200 text-sm mb-3">
                  தமிழ் மொழி ஆதரவு குறித்து எந்தவொரு சிக்கல் இருந்தால், எங்களைத் தொடர்பு கொள்ளவும்:
                </p>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm">
                    {t('help.reportIssue')}
                  </Button>
                  <Button variant="outline" size="sm">
                    {t('help.contactSupport')}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <div className="mt-8 text-center">
        <Card>
          <CardContent className="p-6">
            <h3 className={`font-semibold text-lg mb-3 ${t('language') === 'ta' ? 'tamil-heading' : ''}`}>
              தமிழ் மொழி ஆதரவை மேம்படுத்த உங்கள் கருத்துகள் முக்கியமானவை!
            </h3>
            <p className={`text-gray-600 dark:text-gray-400 mb-4 ${t('language') === 'ta' ? 'tamil-text' : ''}`}>
              இந்தப் பயன்பாட்டின் தமிழ் ஆதரவை மேலும் சிறப்பாக்க, உங்கள் கருத்துகள், புகார்கள், மற்றும் பரிந்துரைகளைப் பகிர்ந்து கொள்ளுங்கள்.
            </p>
            <div className="flex justify-center gap-2">
              <Badge variant="secondary" className="tamil-text">
                தமிழ் வாக்காளர் சரிபார்ப்பு அமைப்பு
              </Badge>
              <Badge variant="secondary" className="tamil-text">
                மொழி உள்ளூர்மயமாக்கம்
              </Badge>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}